// The IANA timezone of an itinerary item / product lives at
// `legs[0].details.startLocation.timezone`. There is no timezone COLUMN — this
// jsonb path is the single source of truth, read by the operations view, the
// WhatsApp sent-time math, the reports and the Location & Time tab.
//
// `legs` is a whole-column jsonb replace on every write (the item update input
// types it as `z.unknown()`), so a caller that sends a `startLocation` without a
// `timezone` silently erases the stored one. These helpers make the write paths
// preserve-by-default: read the zone, stamp it back, and decide whether the
// LOCATION itself actually changed (in which case a new zone must be resolved
// rather than the old one carried over).

/** Minimal view of the leg jsonb these helpers touch. */
type LegLike = {
  details?: {
    startLocation?: Record<string, unknown> | null;
    endLocation?: Record<string, unknown> | null;
  } | null;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/**
 * A zone the timezone math can actually use, else null.
 *
 * Empty / whitespace strings reach us from jsonb (`->> 'timezone'` returns the
 * literal text) and from form payloads, and a stored typo would make `Intl`
 * throw `RangeError` deep inside offset arithmetic — so anything unusable is
 * reported as MISSING instead of being trusted or crashing a caller.
 */
export const normalizeTimezone = (value: unknown): string | null => {
  if (typeof value !== 'string') return null;
  const timezone = value.trim();
  if (timezone.length === 0) return null;
  try {
    // Throws RangeError on an unknown / malformed IANA zone.
    new Intl.DateTimeFormat('en-US', { timeZone: timezone });
    return timezone;
  } catch {
    return null;
  }
};

const firstLeg = (legs: unknown): LegLike | null => {
  if (!Array.isArray(legs) || legs.length === 0) return null;
  return isRecord(legs[0]) ? (legs[0] as LegLike) : null;
};

/** `legs[0].details.startLocation.timezone`, normalized. */
export const readLegStartTimezone = (legs: unknown): string | null =>
  normalizeTimezone(firstLeg(legs)?.details?.startLocation?.timezone);

/** `legs[0].details.endLocation.timezone`, normalized. */
export const readLegEndTimezone = (legs: unknown): string | null =>
  normalizeTimezone(firstLeg(legs)?.details?.endLocation?.timezone);

// A location names a place when any identity field or its coordinates are set,
// as opposed to holding only a seeded `timezone`.
const namesPlace = (location: unknown): boolean => {
  if (!isRecord(location)) return false;
  const place = location;
  const hasIdentity = LOCATION_IDENTITY_KEYS.some((key) => {
    const value = place[key];
    return typeof value === 'string' && value.trim().length > 0;
  });
  return hasIdentity || coordinateKey(place) !== ',';
};

/**
 * The zone an item's END wall-clock time is read in. When the start location
 * names a place and the end names none, the leg is single-location: the end's
 * `timezone` is a seeded placeholder (products default it to Europe/Lisbon), so
 * the end follows the start. Otherwise the end's own zone.
 */
export const resolveLegEndTimezone = (legs: unknown): string | null => {
  const details = firstLeg(legs)?.details;
  if (namesPlace(details?.startLocation) && !namesPlace(details?.endLocation)) {
    return readLegStartTimezone(legs);
  }
  return readLegEndTimezone(legs);
};

/**
 * Return `legs` with `legs[0].details.startLocation.timezone` set to
 * `timezone`, creating the leg / details / startLocation structures when they
 * are absent (mirroring products' `seedLegTimezoneDefaults`, which stamps a
 * `startLocation` that holds only a zone). A leg with just a timezone renders
 * as "no location" exactly as an absent one does, so this is display-safe.
 *
 * No-op when `timezone` is null or the legs already carry a usable zone — the
 * caller's own value always wins over an inherited one.
 */
export const withLegStartTimezone = (
  legs: unknown,
  timezone: string | null
): unknown => {
  const zone = normalizeTimezone(timezone);
  if (!zone) return legs;
  if (readLegStartTimezone(legs)) return legs;

  const source = Array.isArray(legs) ? legs : [];
  const next = source.map((leg) => (isRecord(leg) ? { ...leg } : leg));
  const leg0 = isRecord(next[0])
    ? { ...(next[0] as Record<string, unknown>) }
    : {};
  const details = isRecord(leg0.details) ? { ...leg0.details } : {};
  const startLocation = isRecord(details.startLocation)
    ? { ...details.startLocation }
    : {};

  startLocation.timezone = zone;
  details.startLocation = startLocation;
  leg0.details = details;
  next[0] = leg0;
  return next;
};

// Fields that identify WHERE a location is. `timezone` is deliberately absent:
// it is derived from the place, not part of its identity, so a payload that
// only drops the zone is "the same location" and must keep it.
const LOCATION_IDENTITY_KEYS = [
  'title',
  'formattedAddress',
  'streetAddress',
  'city',
  'country',
  'countryCode',
  'postalCode',
  'placeId',
  'id',
] as const;

const coordinateKey = (location: Record<string, unknown> | null): string => {
  const coords =
    location && isRecord(location.coordinates) ? location.coordinates : null;
  const lat = coords?.latitude ?? location?.latitude;
  const lng = coords?.longitude ?? location?.longitude;
  const num = (v: unknown) =>
    typeof v === 'number' && Number.isFinite(v) ? v.toFixed(6) : '';
  return `${num(lat)},${num(lng)}`;
};

const identityOf = (location: Record<string, unknown> | null): string => {
  if (!location) return '';
  const parts = LOCATION_IDENTITY_KEYS.map((key) => {
    const value = location[key];
    return typeof value === 'string' ? value.trim().toLowerCase() : '';
  });
  return [...parts, coordinateKey(location)].join('|');
};

const startLocationOf = (legs: unknown): Record<string, unknown> | null => {
  const location = firstLeg(legs)?.details?.startLocation;
  return isRecord(location) ? location : null;
};

/**
 * Did the incoming legs move the start location to a DIFFERENT place?
 *
 * `true` → the stored zone belongs to the old place and must not be carried
 * over; resolve one for the new location instead (an explicit clear counts as a
 * change). `false` → same place, so an omitted/blank zone in the payload is a
 * lossy partial update and the stored zone must be preserved.
 */
export const legStartLocationChanged = (
  nextLegs: unknown,
  previousLegs: unknown
): boolean =>
  identityOf(startLocationOf(nextLegs)) !==
  identityOf(startLocationOf(previousLegs));
